import React, { useState, useEffect } from 'react';
import { AppSettings, AudioDevice, SystemDiagnostics } from '@/types';
import { audioService } from '@/services/audioService';
import { DEFAULT_SETTINGS } from '@/services/storageService';
import {
  GoogleService,
  GoogleAccountInfo,
  EmailConfig,
  EmailDispatchLog
} from '@/services/googleService';
import {
  Settings,
  HardDrive,
  Folder,
  Volume2,
  Mic,
  Cpu,
  Monitor,
  ShieldCheck,
  RotateCcw,
  CheckCircle2,
  Sun,
  Moon,
  Info,
  Sliders,
  Sparkles,
  Mail,
  Send,
  Key,
  AtSign,
  Globe,
  Calendar,
  UserCheck,
  Server,
  Check,
  ExternalLink,
  Trash2,
  RefreshCw,
  Video
} from 'lucide-react';

interface SettingsScreenProps {
  settings: AppSettings;
  onUpdateSettings: (settings: Partial<AppSettings>) => void;
  availableDiskSpaceGb: number;
}

export const SettingsScreen: React.FC<SettingsScreenProps> = ({
  settings,
  onUpdateSettings,
  availableDiskSpaceGb
}) => {
  const [activeTab, setActiveTab] = useState<'email_google' | 'hardware'>('email_google');
  const [audioInputs, setAudioInputs] = useState<AudioDevice[]>([]);
  const [diagnostics, setDiagnostics] = useState<SystemDiagnostics | null>(null);
  const [savedBanner, setSavedBanner] = useState(false);

  // Google Account & Email Setup State
  const [accountInfo, setAccountInfo] = useState<GoogleAccountInfo>(GoogleService.getAccountInfo());
  const [emailConfig, setEmailConfig] = useState<EmailConfig>(accountInfo.emailConfig);
  const [testEmailRecipient, setTestEmailRecipient] = useState(accountInfo.email || 'user@gmail.com');
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState<{ success: boolean; message: string } | null>(null);
  const [dispatchLogs, setDispatchLogs] = useState<EmailDispatchLog[]>(GoogleService.getDispatchLogs());
  const [isChangingAccount, setIsChangingAccount] = useState(false);
  const [newEmailInput, setNewEmailInput] = useState(accountInfo.email || '');

  useEffect(() => {
    audioService.getAudioInputDevices().then(setAudioInputs);

    if (window.electronAPI && typeof window.electronAPI.getSystemDiagnostics === 'function') {
      window.electronAPI.getSystemDiagnostics().then(setDiagnostics);
    } else {
      setDiagnostics({
        isElectron: false,
        os: navigator.userAgent.includes('Windows') ? 'Windows (Web Environment)' : 'Web Browser',
        arch: 'x64',
        nodeVersion: 'Browser Standard Runtime',
        electronVersion: 'N/A (Web Mode)',
        ffmpegAvailable: true,
        ffmpegPath: 'Local WebCodecs / MediaStream Engine',
        ffprobeAvailable: true,
        availableDiskSpaceGb: availableDiskSpaceGb,
        totalDiskSpaceGb: 512
      });
    }
  }, [availableDiskSpaceGb]);

  const showSaved = () => {
    setSavedBanner(true);
    setTimeout(() => setSavedBanner(false), 2500);
  };

  const handleSelectFolder = async () => {
    if (window.electronAPI && typeof window.electronAPI.selectDirectory === 'function') {
      const selected = await window.electronAPI.selectDirectory(settings.storagePath);
      if (selected) {
        onUpdateSettings({ storagePath: selected });
        showSaved();
      }
    } else {
      const entered = window.prompt('Enter local storage directory path:', settings.storagePath);
      if (entered) {
        onUpdateSettings({ storagePath: entered });
        showSaved();
      }
    }
  };

  const handleResetDefaults = () => {
    if (window.confirm('Reset all MeetSplit AI preferences to default settings?')) {
      onUpdateSettings(DEFAULT_SETTINGS);
      showSaved();
    }
  };

  // Google Account Actions
  const handleSaveEmailConfig = (updates: Partial<EmailConfig>) => {
    const updatedConfig = { ...emailConfig, ...updates };
    setEmailConfig(updatedConfig);
    const updatedAccount = GoogleService.saveAccountInfo({
      emailConfig: updatedConfig
    });
    setAccountInfo(updatedAccount);
    showSaved();
  };

  const handleConnectOrUpdateAccount = () => {
    if (!newEmailInput.trim()) return;
    const updated = GoogleService.connectAccount(newEmailInput.trim());
    setAccountInfo(updated);
    setEmailConfig(updated.emailConfig);
    setTestEmailRecipient(updated.email);
    setIsChangingAccount(false);
    showSaved();
  };

  const handleDisconnectAccount = () => {
    if (window.confirm('Disconnect Google Account? Meeting invites and attendee email sync will be paused.')) {
      const disconnected = GoogleService.disconnectAccount();
      setAccountInfo(disconnected);
      showSaved();
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailRecipient.trim()) return;
    setIsSendingTestEmail(true);
    setTestEmailResult(null);

    try {
      const res = await GoogleService.sendTestEmail(testEmailRecipient.trim());
      setTestEmailResult(res);
      setDispatchLogs(GoogleService.getDispatchLogs());
    } catch (err: any) {
      setTestEmailResult({
        success: false,
        message: `Failed to send test email: ${err?.message || 'Connection timeout'}`
      });
    } finally {
      setIsSendingTestEmail(false);
    }
  };

  const handleClearLogs = () => {
    GoogleService.clearDispatchLogs();
    setDispatchLogs([]);
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6 ambient-gradient select-none">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-brand-border">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-brand-text">Settings & Integration</h1>
          <p className="text-xs text-brand-muted mt-0.5">
            Manage account integration, email dispatch configuration, and local capture settings
          </p>
        </div>

        <div className="flex items-center gap-3">
          {savedBanner && (
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold animate-in fade-in">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              <span>Settings Saved</span>
            </div>
          )}

          <button
            onClick={handleResetDefaults}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white dark:bg-brand-surface hover:bg-slate-50 border border-brand-border text-xs text-brand-muted hover:text-brand-text transition-colors shadow-sm"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Reset Defaults</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-brand-border pb-3">
        <button
          onClick={() => setActiveTab('email_google')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
            activeTab === 'email_google'
              ? 'bg-indigo-600 text-white shadow-md'
              : 'bg-white dark:bg-brand-surface text-brand-muted hover:text-brand-text border border-brand-border'
          }`}
        >
          <Mail className="w-4 h-4" />
          <span>Calendar & Email Dispatch Setup</span>
        </button>

        <button
          onClick={() => setActiveTab('hardware')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
            activeTab === 'hardware'
              ? 'bg-indigo-600 text-white shadow-md'
              : 'bg-white dark:bg-brand-surface text-brand-muted hover:text-brand-text border border-brand-border'
          }`}
        >
          <HardDrive className="w-4 h-4" />
          <span>Local Hardware, Storage & AI</span>
        </button>
      </div>

      {/* TAB 1: CALENDAR & EMAIL SETUP */}
      {activeTab === 'email_google' && (
        <div className="space-y-6">
          {/* SECTION 1: GOOGLE ACCOUNT CONNECTION */}
          <div className="p-6 rounded-3xl bg-white dark:bg-brand-surface border border-brand-border shadow-card space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-brand-border">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-200 text-indigo-600 flex items-center justify-center font-bold text-lg shadow-sm">
                  {accountInfo.isConnected ? accountInfo.name.charAt(0).toUpperCase() : 'G'}
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-brand-text">
                      {accountInfo.isConnected ? accountInfo.name : 'Google Account Not Connected'}
                    </h3>
                    {accountInfo.isConnected ? (
                      <span className="px-2.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        Connected & Synced
                      </span>
                    ) : (
                      <span className="px-2.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold">
                        Disconnected
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-brand-muted mt-0.5 font-mono">
                    {accountInfo.isConnected ? accountInfo.email : 'Link your Google account to sync Meet meetings & send clips'}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {accountInfo.isConnected ? (
                  <>
                    <button
                      onClick={() => {
                        setNewEmailInput(accountInfo.email);
                        setIsChangingAccount(true);
                      }}
                      className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-brand-text text-xs font-semibold transition-colors"
                    >
                      Switch Email
                    </button>
                    <button
                      onClick={handleDisconnectAccount}
                      className="px-3 py-1.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 text-xs font-semibold transition-colors border border-rose-200"
                    >
                      Disconnect
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setNewEmailInput('host@gmail.com');
                      setIsChangingAccount(true);
                    }}
                    className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-md transition-colors flex items-center gap-2"
                  >
                    <Video className="w-3.5 h-3.5" />
                    <span>Connect Google Account</span>
                  </button>
                )}
              </div>
            </div>

            {/* Account Switch Dialog / Modal Inline */}
            {isChangingAccount && (
              <div className="p-4 rounded-2xl bg-indigo-50/60 border border-indigo-200 space-y-3">
                <h4 className="text-xs font-bold text-indigo-900">Connect Google Account Email</h4>
                <div className="flex items-center gap-2">
                  <input
                    type="email"
                    value={newEmailInput}
                    onChange={(e) => setNewEmailInput(e.target.value)}
                    placeholder="Enter your Gmail address (e.g., host@gmail.com)"
                    className="flex-1 bg-white border border-indigo-300 rounded-xl px-3 py-2 text-xs text-brand-text font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    onClick={handleConnectOrUpdateAccount}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-colors"
                  >
                    Save & Connect
                  </button>
                  <button
                    onClick={() => setIsChangingAccount(false)}
                    className="px-3 py-2 bg-white text-brand-muted hover:text-brand-text rounded-xl text-xs font-semibold border border-brand-border"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Sync Toggles */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div className="flex items-center justify-between p-3.5 rounded-2xl bg-slate-50 dark:bg-brand-elevated/40 border border-brand-border">
                <div className="space-y-0.5">
                  <span className="font-bold text-brand-text flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-indigo-500" />
                    Calendar Meeting Sync
                  </span>
                  <p className="text-[11px] text-brand-muted">
                    Automatically pulls upcoming meeting events and attendee emails
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={accountInfo.calendarSyncEnabled}
                  onChange={(e) => {
                    const updated = GoogleService.saveAccountInfo({ calendarSyncEnabled: e.target.checked });
                    setAccountInfo(updated);
                    showSaved();
                  }}
                  className="accent-indigo-600 w-4 h-4 rounded cursor-pointer"
                />
              </div>

              <div className="flex items-center justify-between p-3.5 rounded-2xl bg-slate-50 dark:bg-brand-elevated/40 border border-brand-border">
                <div className="space-y-0.5">
                  <span className="font-bold text-brand-text flex items-center gap-1.5">
                    <Send className="w-3.5 h-3.5 text-teal-600" />
                    Auto-Send Tracks on Export
                  </span>
                  <p className="text-[11px] text-brand-muted">
                    Automatically sends isolated MP4 clips to attendee emails once rendered
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={emailConfig.autoEmailOnExport}
                  onChange={(e) => handleSaveEmailConfig({ autoEmailOnExport: e.target.checked })}
                  className="accent-indigo-600 w-4 h-4 rounded cursor-pointer"
                />
              </div>
            </div>
          </div>

          {/* SECTION 2: EMAIL PROVIDER & SMTP CONFIG */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Email Dispatch Provider & Server Details */}
            <div className="p-6 rounded-3xl bg-white dark:bg-brand-surface border border-brand-border shadow-card space-y-4">
              <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider flex items-center gap-2">
                <Server className="w-4 h-4 text-indigo-600" />
                Email Delivery Protocol & Provider
              </h3>

              {/* Provider Selector */}
              <div className="grid grid-cols-2 gap-2 bg-slate-100 dark:bg-brand-elevated p-1 rounded-2xl border border-brand-border">
                <button
                  onClick={() => handleSaveEmailConfig({ provider: 'gmail' })}
                  className={`py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                    emailConfig.provider === 'gmail'
                      ? 'bg-white dark:bg-brand-surface text-indigo-600 shadow-sm'
                      : 'text-brand-muted hover:text-brand-text'
                  }`}
                >
                  <Mail className="w-3.5 h-3.5" />
                  <span>Google / Gmail Direct</span>
                </button>

                <button
                  onClick={() => handleSaveEmailConfig({ provider: 'smtp' })}
                  className={`py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                    emailConfig.provider === 'smtp'
                      ? 'bg-white dark:bg-brand-surface text-indigo-600 shadow-sm'
                      : 'text-brand-muted hover:text-brand-text'
                  }`}
                >
                  <Server className="w-3.5 h-3.5" />
                  <span>Custom SMTP Server</span>
                </button>
              </div>

              {/* Sender Details */}
              <div className="space-y-3 text-xs">
                <div className="space-y-1">
                  <label className="font-semibold text-brand-text">Sender Display Name</label>
                  <input
                    type="text"
                    value={emailConfig.senderName}
                    onChange={(e) => handleSaveEmailConfig({ senderName: e.target.value })}
                    className="w-full bg-slate-50 dark:bg-brand-elevated border border-brand-border rounded-xl px-3 py-2 text-xs text-brand-text font-medium focus:outline-none"
                    placeholder="e.g. Host via MeetSplit AI"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-brand-text">Sender Email Address</label>
                  <input
                    type="email"
                    value={emailConfig.senderEmail}
                    onChange={(e) => handleSaveEmailConfig({ senderEmail: e.target.value })}
                    className="w-full bg-slate-50 dark:bg-brand-elevated border border-brand-border rounded-xl px-3 py-2 text-xs font-mono text-brand-text focus:outline-none"
                    placeholder="e.g. host@gmail.com"
                  />
                </div>

                {/* SMTP Server Details (shown if provider === 'smtp') */}
                {emailConfig.provider === 'smtp' ? (
                  <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-brand-elevated/40 border border-brand-border space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2 space-y-1">
                        <label className="text-[11px] font-semibold text-brand-muted">SMTP Server Host</label>
                        <input
                          type="text"
                          value={emailConfig.smtpHost}
                          onChange={(e) => handleSaveEmailConfig({ smtpHost: e.target.value })}
                          className="w-full bg-white dark:bg-brand-surface border border-brand-border rounded-lg px-2.5 py-1.5 text-xs font-mono text-brand-text"
                          placeholder="smtp.gmail.com"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[11px] font-semibold text-brand-muted">Port</label>
                        <input
                          type="number"
                          value={emailConfig.smtpPort}
                          onChange={(e) => handleSaveEmailConfig({ smtpPort: parseInt(e.target.value, 10) || 587 })}
                          className="w-full bg-white dark:bg-brand-surface border border-brand-border rounded-lg px-2.5 py-1.5 text-xs font-mono text-brand-text"
                          placeholder="587"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[11px] font-semibold text-brand-muted">SMTP Username / App Password</label>
                      <input
                        type="password"
                        value={emailConfig.smtpPassword || ''}
                        onChange={(e) => handleSaveEmailConfig({ smtpPassword: e.target.value })}
                        className="w-full bg-white dark:bg-brand-surface border border-brand-border rounded-lg px-2.5 py-1.5 text-xs font-mono text-brand-text"
                        placeholder="Google App Password (16 characters)"
                      />
                    </div>

                    <div className="flex items-center justify-between text-xs pt-1">
                      <span className="text-[11px] text-brand-muted">Enable TLS / SSL Security</span>
                      <input
                        type="checkbox"
                        checked={emailConfig.useSslTls}
                        onChange={(e) => handleSaveEmailConfig({ useSslTls: e.target.checked })}
                        className="accent-indigo-600 rounded cursor-pointer"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="p-3.5 rounded-2xl bg-indigo-50/50 border border-indigo-100 text-indigo-900 text-xs space-y-1">
                    <span className="font-bold flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-indigo-600" />
                      Gmail Direct Delivery Active
                    </span>
                    <p className="text-[11px] text-indigo-700 leading-relaxed">
                      Outgoing emails are sent directly through your authenticated Google Account ({accountInfo.email}) without requiring custom mail server ports.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Email Template & Live Preview */}
            <div className="p-6 rounded-3xl bg-white dark:bg-brand-surface border border-brand-border shadow-card space-y-4">
              <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider flex items-center gap-2">
                <AtSign className="w-4 h-4 text-purple-600" />
                Email Template & Live Attendee Preview
              </h3>

              <div className="space-y-3 text-xs">
                <div className="space-y-1">
                  <label className="font-semibold text-brand-text">Subject Template</label>
                  <input
                    type="text"
                    value={emailConfig.emailSubjectTemplate}
                    onChange={(e) => handleSaveEmailConfig({ emailSubjectTemplate: e.target.value })}
                    className="w-full bg-slate-50 dark:bg-brand-elevated border border-brand-border rounded-xl px-3 py-2 text-xs text-brand-text font-medium focus:outline-none"
                  />
                  <p className="text-[10px] text-brand-muted">Available tags: {'{meeting_title}'}, {'{name}'}, {'{file_name}'}</p>
                </div>

                {/* Email Mock Preview Card */}
                <div className="p-4 rounded-2xl bg-slate-50 dark:bg-brand-elevated/30 border border-brand-border space-y-2">
                  <div className="flex items-center justify-between text-[11px] text-brand-muted border-b border-brand-border pb-2">
                    <span>To: <strong>Speaker 2 (speaker2@gmail.com)</strong></span>
                    <span className="text-[10px]">Preview</span>
                  </div>

                  <div className="text-xs font-bold text-brand-text">
                    [MeetSplit AI] Your isolated video track from Meeting Discussion is ready
                  </div>

                  <div className="text-[11px] text-brand-muted whitespace-pre-line bg-white dark:bg-brand-surface p-3 rounded-xl border border-brand-border font-sans leading-relaxed">
                    Hi Attendee,
                    {'\n\n'}
                    Your isolated speaker track (Speaker2_Isolated_Track.mp4) from our meeting call "Team Discussion" has been extracted and is ready for download.
                    {'\n\n'}
                    ▶ Download Isolated Track (148 MB • 1080p H.264)
                    {'\n\n'}
                    Best regards,{'\n'}MeetSplit AI Studio
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 3: TEST EMAIL DISPATCH TOOL */}
          <div className="p-6 rounded-3xl bg-white dark:bg-brand-surface border border-brand-border shadow-card space-y-4">
            <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider flex items-center gap-2">
              <Send className="w-4 h-4 text-emerald-600" />
              Dispatch Verification & Test Email
            </h3>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <input
                type="email"
                value={testEmailRecipient}
                onChange={(e) => setTestEmailRecipient(e.target.value)}
                placeholder="Enter recipient email (e.g., recipient@gmail.com)"
                className="flex-1 bg-slate-50 dark:bg-brand-elevated border border-brand-border rounded-2xl px-3.5 py-2.5 text-xs font-mono text-brand-text focus:outline-none"
              />

              <button
                disabled={isSendingTestEmail}
                onClick={handleSendTestEmail}
                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl text-xs font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95"
              >
                {isSendingTestEmail ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Dispatching Test Email...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" />
                    <span>Send Test Email</span>
                  </>
                )}
              </button>
            </div>

            {testEmailResult && (
              <div
                className={`p-3.5 rounded-2xl border text-xs font-semibold flex items-center gap-2 ${
                  testEmailResult.success
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : 'bg-rose-50 border-rose-200 text-rose-800'
                }`}
              >
                {testEmailResult.success ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                ) : (
                  <Info className="w-4 h-4 text-rose-600 shrink-0" />
                )}
                <span>{testEmailResult.message}</span>
              </div>
            )}
          </div>

          {/* SECTION 4: EMAIL DISPATCH LOG */}
          <div className="p-6 rounded-3xl bg-white dark:bg-brand-surface border border-brand-border shadow-card space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider flex items-center gap-2">
                <Mail className="w-4 h-4 text-indigo-600" />
                Recent Email Transmission History ({dispatchLogs.length})
              </h3>

              {dispatchLogs.length > 0 && (
                <button
                  onClick={handleClearLogs}
                  className="flex items-center gap-1 text-[11px] text-brand-muted hover:text-rose-600 transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                  <span>Clear History</span>
                </button>
              )}
            </div>

            {dispatchLogs.length === 0 ? (
              <p className="text-xs text-brand-muted italic py-2">No emails dispatched yet.</p>
            ) : (
              <div className="space-y-2">
                {dispatchLogs.slice(0, 5).map((log) => (
                  <div
                    key={log.id}
                    className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 dark:bg-brand-elevated/40 border border-brand-border text-xs"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-xl bg-indigo-50 border border-indigo-200 text-indigo-600 flex items-center justify-center font-bold text-xs">
                        {log.recipientName.charAt(0).toUpperCase()}
                      </div>

                      <div>
                        <div className="font-semibold text-brand-text">{log.recipientName} ({log.recipientEmail})</div>
                        <div className="text-[10px] text-brand-muted font-mono">{log.trackFileName} • {log.meetingTitle}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold">
                        Sent ✓
                      </span>
                      <span className="text-[10px] text-brand-muted font-mono">
                        {new Date(log.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 2: HARDWARE, STORAGE & CV SENSITIVITY */}
      {activeTab === 'hardware' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* SECTION 1: Storage & Directories */}
          <div className="p-5 rounded-3xl bg-white dark:bg-brand-surface border border-brand-border shadow-card space-y-4">
            <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider flex items-center gap-2">
              <HardDrive className="w-4 h-4 text-brand-secondary" />
              Local Storage & Output Directory
            </h3>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-brand-text">Default Recordings Folder</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={settings.storagePath}
                    onChange={(e) => onUpdateSettings({ storagePath: e.target.value })}
                    className="flex-1 bg-slate-50 dark:bg-brand-elevated border border-brand-border rounded-xl px-3 py-2 text-xs font-mono text-brand-text focus:outline-none"
                  />
                  <button
                    onClick={handleSelectFolder}
                    className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 border border-brand-border rounded-xl text-xs font-semibold text-brand-text"
                  >
                    Browse
                  </button>
                </div>
              </div>

              <div className="p-3 rounded-2xl bg-slate-50 dark:bg-brand-elevated/40 border border-brand-border text-xs text-brand-muted space-y-1.5">
                <div className="flex justify-between">
                  <span>Free Disk Space</span>
                  <span className="font-mono text-brand-text font-bold">{availableDiskSpaceGb} GB</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-brand-elevated h-1.5 rounded-full overflow-hidden">
                  <div
                    className="bg-emerald-500 h-full rounded-full"
                    style={{ width: `${Math.min(100, Math.max(10, (availableDiskSpaceGb / 512) * 100))}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 2: Audio Preferences */}
          <div className="p-5 rounded-3xl bg-white dark:bg-brand-surface border border-brand-border shadow-card space-y-4">
            <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider flex items-center gap-2">
              <Volume2 className="w-4 h-4 text-brand-accent" />
              Audio Preferences & Loopback
            </h3>

            <div className="space-y-3 text-xs">
              <div className="space-y-1.5">
                <label className="font-semibold text-brand-text flex items-center gap-1.5">
                  <Mic className="w-3.5 h-3.5 text-brand-secondary" />
                  Default Microphone Device
                </label>
                <select
                  value={settings.defaultMicId}
                  onChange={(e) => {
                    onUpdateSettings({ defaultMicId: e.target.value });
                    showSaved();
                  }}
                  className="w-full bg-slate-50 dark:bg-brand-elevated border border-brand-border rounded-xl px-3 py-2 text-xs text-brand-text"
                >
                  {audioInputs.map(d => (
                    <option key={d.deviceId} value={d.deviceId}>{d.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 dark:bg-brand-elevated/40 border border-brand-border">
                <div>
                  <h4 className="font-semibold text-brand-text">System Audio Capture Loopback</h4>
                  <p className="text-[11px] text-brand-muted mt-0.5">Captures remote attendee voices</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.enableSystemAudio}
                  onChange={(e) => {
                    onUpdateSettings({ enableSystemAudio: e.target.checked });
                    showSaved();
                  }}
                  className="accent-indigo-600 w-4 h-4 rounded cursor-pointer"
                />
              </div>
            </div>
          </div>

          {/* SECTION 3: Computer Vision & AI Sensitivity */}
          <div className="p-5 rounded-3xl bg-white dark:bg-brand-surface border border-brand-border shadow-card space-y-4">
            <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider flex items-center gap-2">
              <Sliders className="w-4 h-4 text-purple-600" />
              Local Computer-Vision Pipeline
            </h3>

            <div className="space-y-4 text-xs">
              <div>
                <div className="flex justify-between font-semibold text-brand-text mb-1">
                  <span>Tile Grid Detection Sensitivity</span>
                  <span className="font-mono text-indigo-600 font-bold">{settings.cvSensitivity}/10</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={settings.cvSensitivity}
                  onChange={(e) => {
                    onUpdateSettings({ cvSensitivity: parseInt(e.target.value, 10) });
                    showSaved();
                  }}
                  className="w-full h-1.5 bg-slate-200 dark:bg-brand-elevated rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
                <p className="text-[10px] text-brand-muted mt-1">
                  Higher sensitivity detects subtle grid lines and small attendee tiles.
                </p>
              </div>

              <div className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 dark:bg-brand-elevated/40 border border-brand-border">
                <div>
                  <h4 className="font-semibold text-brand-text">Auto-Process On Recording Finish</h4>
                  <p className="text-[11px] text-brand-muted mt-0.5">Automatically begins offline participant detection</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.autoProcessAfterRecording}
                  onChange={(e) => {
                    onUpdateSettings({ autoProcessAfterRecording: e.target.checked });
                    showSaved();
                  }}
                  className="accent-indigo-600 w-4 h-4 rounded cursor-pointer"
                />
              </div>
            </div>
          </div>

          {/* SECTION 4: Diagnostics & Privacy Policy */}
          <div className="p-5 rounded-3xl bg-white dark:bg-brand-surface border border-brand-border shadow-card space-y-4">
            <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              Privacy Verification & Diagnostics
            </h3>

            <div className="space-y-2 text-xs">
              <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-900 space-y-1">
                <span className="font-bold flex items-center gap-1.5 text-emerald-700">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Zero Cloud Video Telemetry Guarantee
                </span>
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  MeetSplit AI operates entirely within your local device boundaries. No video frames, audio tracks, or participant crops are ever uploaded to any third party.
                </p>
              </div>

              {diagnostics && (
                <div className="p-3 rounded-2xl bg-slate-50 dark:bg-brand-elevated/40 border border-brand-border font-mono text-[11px] space-y-1 text-brand-muted">
                  <div>OS: <span className="text-brand-text">{diagnostics.os}</span></div>
                  <div>Node: <span className="text-brand-text">{diagnostics.nodeVersion}</span></div>
                  <div>FFmpeg: <span className="text-brand-text">{diagnostics.ffmpegAvailable ? 'Operational' : 'Not Found'}</span></div>
                  <div className="truncate">Binary: <span className="text-brand-text">{diagnostics.ffmpegPath}</span></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
